import React, { useState, useEffect, SetStateAction, Dispatch } from "react";
import { Box, Button, IconButton, Typography, Grid } from "@mui/material";
import TableComponent from "../../components/TableComponent";
import { GridColDef, GridAlignment } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import { ENUM_ROUTES } from "../../RoutesEnum";
import {
  useDeleteBannerMutation,
  useLazyGetBannersQuery,
  useUpdateBannerMutation,
} from "../../services/banner";
import { showToast, showError } from "../../constants/toasts";
import CustomToggle from "../../helpers/CustomToggle";
import { ConfirmationModal } from "../../Modals/ConfirmationModal";

interface BannerListProps {
  searchTerm: string;
  setSearchTerm: Dispatch<SetStateAction<string>>;
  setDebouncedSearchTerm: Dispatch<SetStateAction<string>>;
}

const BannerList = ({
  searchTerm,
  setDebouncedSearchTerm,
  setSearchTerm,
}: BannerListProps) => {
  const navigate = useNavigate();
  const [bannerData, setBannerData] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [deleteBanner] = useDeleteBannerMutation();
  const [selectedId, setSelectedId] = useState("");
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const [getBanners, { isLoading, error }] = useLazyGetBannersQuery();
  const [updateBanner] = useUpdateBannerMutation();

  const fetchBanners = async () => {
    try {
      const response = await getBanners({
        from: page,
        size: pageSize,
        search: searchTerm,
      }).unwrap();

      if (response?.statusCode === 200) {
        setBannerData(response.data.data || []);
        setTotalCount(response.data.total || 0);
      }
    } catch (error: any) {
      showError(error?.data?.message || "Failed to fetch banners");
    }
  };

  const deleteBannerApi = async (id: string) => {
    setLoading(true);
    try {
      const response = await deleteBanner({
        id,
      }).unwrap();
      setLoading(false);
      if (response?.statusCode === 200) {
        showToast(response?.message || "Banner Deleted Successfully");
        fetchBanners();
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
      setLoading(false);
    }
  };

  const toggleStatus = async (id: string, currentStatus: boolean) => {
    try {
      const response = await updateBanner({
        id,
        body: { isActive: !currentStatus },
      }).unwrap();

      if (response?.statusCode === 200) {
        showToast(
          `Banner ${!currentStatus ? "activated" : "deactivated"} successfully`
        );
        fetchBanners();
      } else {
        showError("Failed to update banner status");
      }
    } catch (error: any) {
      showError(error?.data?.message || "Failed to update banner status");
    }
  };
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };
  useEffect(() => {
    fetchBanners();
  }, [page, pageSize, searchTerm]);
  const columns: GridColDef<any>[] = [
    {
      field: "image",
      headerName: "Banner",
      flex: 1,
      minWidth: 120,
      sortable: false,
      renderCell: (params: any) => (
        <img
          src={params.value || "/static/images/picture.png"}
          alt="banner"
          style={{
            width: "60%",
            maxWidth: 100,
            height: 40,
            objectFit: "cover",
            borderRadius: 8,
          }}
          onError={(e) => {
            e.currentTarget.src = "/static/images/picture.png";
          }}
        />
      ),
    },
    {
      field: "title",
      headerName: "Title",
      flex: 1,
      minWidth: 150,
      sortable: false,
    },
    {
      field: "venueStorage",
      headerName: "Venue",
      flex: 1,
      minWidth: 200,
      editable: false,
      sortable: false,
      renderCell: (params) => {
        const venueStorage = params?.value;
        if (Array.isArray(venueStorage) && venueStorage.length > 0) {
          const venueNames = venueStorage.map((venue) => venue.name).join(", ");
          return <span>{venueNames}</span>;
        }
        return <span>-</span>;
      },
    },
    // {
    //   field: "locations",
    //   headerName: "Locations",
    //   flex: 1,
    //   minWidth: 150,
    //   renderCell: (params: any) => {
    //     const locations = params.value;
    //     if (Array.isArray(locations)) {
    //       return locations.join(", ");
    //     }
    //     return locations || "-";
    //   },
    //   sortable: false,
    // },
    {
      field: "addFrequency",
      headerName: "Frequency",
      flex: 1,
      minWidth: 190,
      editable: false,
      sortable: false,
      renderCell: (params) => (
        <span>{params?.value || "-"}(Every X Hours)</span>
      ),
    },
    {
      field: "duration",
      headerName: "Duration",
      flex: 1,
      minWidth: 100,
      sortable: false,
    },
    {
      field: "startDate",
      headerName: "Start Date",
      flex: 1,
      minWidth: 120,
      sortable: false,
      renderCell: (params: any) => {
        if (!params.value) return "-";
        return new Date(params.value).toLocaleDateString();
      },
    },
    {
      field: "endDate",
      headerName: "End Date",
      flex: 1,
      minWidth: 120,
      sortable: false,
      renderCell: (params: any) => {
        if (!params.value) return "-";
        return new Date(params.value).toLocaleDateString();
      },
    },
    {
      field: "isActive",
      headerName: "Active",
      align: "center" as GridAlignment,
      headerAlign: "center" as GridAlignment,
      flex: 1,
      minWidth: 100,
      sortable: false,
      renderCell: (params: any) => (
        <CustomToggle
          checked={params.value || false}
          onChange={() =>
            toggleStatus(params.row._id || params.row.id, params.value)
          }
          id={params.row._id || params.row.id}
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      minWidth: 130,
      sortable: false,
      renderCell: (params: any) => (
        <Box className="table_actions v2">
          <IconButton
            onClick={() =>
              navigate(
                `${ENUM_ROUTES.BANNERS_DETAILS}/${
                  params?.row?._id || params?.row?.id
                }`
              )
            }
          >
            <img src="/static/images/eye_icon.svg" alt="View" />
          </IconButton>
          <IconButton
            onClick={() =>
              navigate(`${ENUM_ROUTES.EDIT_BANNERS}/${params?.row?._id}`)
            }
          >
            <img src="/static/images/edit2_icon.svg" alt="Edit" />
          </IconButton>
          <IconButton
            onClick={() => {
              setSelectedId(params?.row?._id);
              setOpen(true);
            }}
          >
            <img src="/static/images/trash2_icon.svg" alt="Delete" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <div className="sorting_table">
      <TableComponent
        columns={columns}
        rows={bannerData}
        count={totalCount}
        page={page}
        onPageChange={handlePageChange}
        loading={isLoading}
      />
      <ConfirmationModal
        text={"Are you sure you want to delete this Banner?"}
        openModal={open}
        setOpenModal={setOpen}
        handleSubmit={() => deleteBannerApi(selectedId)}
      />
    </div>
  );
};

export default BannerList;
